/** Fire-code setback presets on the Rules page (IFC 2021 residential, Canada BC, none …): picking one fills the
 * company's setbacks draft, which is then saved (or discarded) like any edit. */
import { useQuery } from "@tanstack/react-query";
import { api, unwrap, type S } from "../api/client";
import { Field, Select } from "../components/ui";
import { useT } from "../i18n/I18nProvider";

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function SetbackPresetPicker({ orgId, current, onPick, disabled }: { orgId: string; current: S["SetbacksSettings"]; onPick: (d: S["SetbacksSettings"]) => void; disabled?: boolean }) {
  const t = useT();
  const presets = useQuery({
    queryKey: ["setback-presets", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/setback-presets", { params: { path: { org_id: orgId } } })),
    staleTime: 3600_000,
  });
  const items = presets.data?.items ?? [];
  const match = items.find((p) => same({ ...p.document, units: "m" }, { ...current, units: "m" }));
  const value = match?.slug ?? "custom";
  return (
    <Field
      label={t("designer.setbackPresets.label")}
      hint={match ? (match.source ? t("designer.setbackPresets.hintWithSource", { description: match.description ?? "", source: match.source }) : (match.description ?? "")) : t("designer.setbackPresets.customHint")}
    >
      <Select
        ariaLabel={t("designer.setbackPresets.label")}
        value={value}
        disabled={disabled || !items.length}
        onChange={(slug) => {
          const p = items.find((x) => x.slug === slug);
          if (p) onPick(structuredClone(p.document));
        }}
        options={[...items.map((p) => ({ value: p.slug, label: p.name })), ...(match ? [] : [{ value: "custom", label: t("designer.setbackPresets.custom") }])]}
      />
    </Field>
  );
}
